import classNames from "classnames";
import { t } from "i18next";
import type { MutableRefObject } from "react";
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";

import type { ISuggestion } from "@repo/shopify/hooks/useGooglePlacesAutocomplete";
import { useGooglePlacesAutocomplete } from "@repo/shopify/hooks/useGooglePlacesAutocomplete";
import type { MailingAddressInput } from "@repo/shopify/types/generated/storefront.types";
import { parseGooglePlaceAddress } from "@repo/shopify/utils/googlePlaces";

import Input from "@atoms/Input";
import Select from "@atoms/Select";

import AddressSuggestions from "@molecules/AddressSuggestions";

import useAddress from "@hooks/useAddress";
import useFormErrorHandler from "@hooks/useFormErrorHandler";

export interface IBillingAddressIntlHandle {
  validate: () => Promise<boolean>;
}

interface IProps {
  billingAddressRef: MutableRefObject<MailingAddressInput | undefined>;
}

interface IFormMailingAddress extends MailingAddressInput {
  countryCode: string;
  provinceCode: string;
}

const BillingAddressIntl = forwardRef<IBillingAddressIntlHandle, IProps>(function BillingAddressIntl({ billingAddressRef }: IProps, ref) {
  const formRef = useRef<HTMLFormElement>(null);
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue: setFormValue,
    formState: { errors },
    getValues,
    clearErrors,
    trigger,
  } = useForm<IFormMailingAddress>({
    defaultValues: {
      countryCode: "US",
      firstName: "",
      lastName: "",
      address1: "",
      address2: "",
      city: "",
      company: "",
      phone: "",
      provinceCode: "-1",
      province: "",
      zip: "",
    },
  });

  const watchedCountryCode = watch("countryCode");

  const { suggestions, fetchPlaceDetails, isSuggestionsVisible, setIsSuggestionsVisible, handleInputChange, clearSuggestions, clearCache } =
    useGooglePlacesAutocomplete({
      countryCode: watchedCountryCode,
    });

  const { countryOptions, stateOptions, formOrder } = useAddress({ countryCode: watchedCountryCode });

  const { onCommonError, displayError } = useFormErrorHandler({ values: getValues(), errors });

  /** 선택한 국가에 province 필드가 없는 경우 처리 */
  useEffect(() => {
    if (!formOrder.length) return;
    const hasProvince = formOrder?.flat().some((item) => item.key === "province");
    if (!hasProvince) {
      setFormValue("provinceCode", "-1");
      setFormValue("province", "");
    }
  }, [formOrder, setFormValue]);

  /** 국가 변경 시 폼 에러 초기화 */
  useEffect(() => {
    clearErrors();
  }, [watchedCountryCode, clearErrors]);

  const onSubmit: SubmitHandler<IFormMailingAddress> = (formData) => {
    const { countryCode, provinceCode, ...restData } = formData;
    const countryName = countryOptions.find((option) => option.value === countryCode)?.name ?? countryCode;
    billingAddressRef.current = {
      ...restData,
      country: countryName,
      province: provinceCode,
    };
  };

  useImperativeHandle(ref, () => ({
    validate: () =>
      new Promise<boolean>((resolve) => {
        handleSubmit(
          (formData) => {
            onSubmit(formData);
            resolve(true);
          },
          (formErrors) => {
            onCommonError(formErrors);
            resolve(false);
          },
        )();
      }),
  }));

  /** 사용자가 제안된 장소를 선택할 때 호출되며, 선택된 장소의 세부 정보 가져옴 */
  const handleSelect = (suggestion: ISuggestion) => async () => {
    clearSuggestions();
    const { mainText, place } = suggestion;

    // 장소의 세부 정보를 가져오기
    const addressComponents = await fetchPlaceDetails(place);
    if (!addressComponents) return;

    // Google Place 주소 파싱
    const parsedAddress = parseGooglePlaceAddress(addressComponents, stateOptions);

    setFormValue("address1", mainText, { shouldValidate: true });
    setFormValue("city", parsedAddress.city || "", { shouldValidate: true });
    setFormValue("provinceCode", parsedAddress.provinceCode || "-1", { shouldValidate: true });
    setFormValue("province", parsedAddress.provinceName || "", { shouldValidate: true });
    setFormValue("zip", parsedAddress.zipCode || "", { shouldValidate: true });

    // 선택 후 세션 토큰 갱신
    clearCache();
  };

  return (
    <form
      className="tnt-gap-x pc:w-[calc(100%+2.5rem)] pc:-translate-x-5 pc:px-5 grid grid-cols-6 gap-x-2.5 gap-y-2.5"
      onSubmit={handleSubmit(onSubmit, onCommonError)}
      ref={formRef}
    >
      <div className="col-span-8 flex flex-col gap-2.5">
        <Select
          as="form"
          id="countryCode"
          label="Country"
          {...register("countryCode")}
          onChange={(e) => {
            setFormValue("countryCode", e.target.value);
            setFormValue("address1", "");
            setFormValue("address2", "");
            setFormValue("city", "");
            setFormValue("company", "");
            setFormValue("phone", "");
            setFormValue("provinceCode", "-1");
            setFormValue("province", "");
            setFormValue("zip", "");
          }}
          options={countryOptions}
        />
      </div>
      <div className="tnt-gap-x col-span-8 grid grid-cols-6 gap-y-2.5">
        {formOrder?.map((formRow) => {
          return formRow.map((form) => (
            <div
              className={classNames(
                formRow.length === 1 && "col-span-6",
                formRow.length === 2 && "col-span-3",
                formRow.length === 3 && "col-span-2",
                "relative",
              )}
              key={form.key}
            >
              {form.key !== "country" && form.key !== "province" && form.key !== "address1" && (
                <Input
                  id={form.key}
                  label={form.name}
                  placeholder={form.name}
                  {...register(form.key, {
                    validate: (value) => {
                      if (form.required !== false) {
                        if (form.key === "address2") {
                          return true;
                        }
                        if (!value?.trim()) return t(`necessary_${form.key}`);
                      } else {
                        return true;
                      }
                    },
                    onChange: (e) => {
                      if (form.key === "country" || form.key === "province" || form.key === "address1") return;
                      setFormValue(form.key, e.target.value, { shouldValidate: true });
                    },
                  })}
                  errorMessage={displayError(form.key)?.errorMessage ?? ""}
                  isError={displayError(form.key)?.isError}
                />
              )}
              {form.key === "province" && (
                <Select
                  as="form"
                  id={form.key}
                  label={form.name}
                  options={stateOptions}
                  {...register("provinceCode", {
                    validate: (value) => {
                      if (value === "-1") return t(`necessary_province`);
                      return true;
                    },
                  })}
                  onChange={(e) => {
                    setFormValue("provinceCode", e.target.value, { shouldValidate: true });
                    const matchedProvince = stateOptions.find((option) => option.value === e.target.value);
                    if (matchedProvince) {
                      setFormValue("province", matchedProvince.name, { shouldValidate: true });
                    }
                  }}
                />
              )}
              {form.key === "address1" && (
                <React.Fragment key={form.key}>
                  <Controller
                    control={control}
                    name={form.key}
                    render={({ field }) => (
                      <Input
                        {...field}
                        autoComplete="new-password"
                        errorMessage={displayError(form.key)?.errorMessage ?? ""}
                        isError={displayError(form.key)?.isError}
                        label={form.name}
                        onBlur={() => {
                          setIsSuggestionsVisible(false);
                        }}
                        onChange={(e) => {
                          clearSuggestions();
                          clearErrors("address1");
                          trigger("address1").then((isValid) => {
                            if (!isValid) {
                              const nonEnglishRegex = /[^\w\s\p{P}]/u;
                              if (nonEnglishRegex.test(e.target.value)) {
                                displayError(form.key);
                              }
                            }
                          });
                          handleInputChange(e.target.value);
                          setFormValue("address1", e.target.value, { shouldValidate: true });
                          field.onChange(e);
                        }}
                        onFocus={() => {
                          setIsSuggestionsVisible(true);
                        }}
                        placeholder={form.name}
                        value={getValues("address1") || ""}
                      />
                    )}
                    rules={{
                      validate: (value) => {
                        if (!value?.trim()) {
                          return t(`necessary_${form.key}`);
                        }
                        const nonEnglishRegex = /[^\w\s\p{P}]/u;
                        if (nonEnglishRegex.test(value)) {
                          return `${form.name} ${t(`invalid_english`)}`;
                        }
                        return true;
                      },
                    }}
                  />
                  {isSuggestionsVisible && suggestions.length !== 0 ? (
                    <AddressSuggestions onSelect={handleSelect} suggestions={suggestions} />
                  ) : null}
                </React.Fragment>
              )}
            </div>
          ));
        })}
      </div>
    </form>
  );
});

export default BillingAddressIntl;
